/**
 * The Price Brief (issue 552 redesign) — a single-screen pricing-intelligence
 * document: the market's answer first, the evidence beneath it, and the
 * asking price as a live marker the agent can move in front of the client.
 *
 * Division of labour (mirrors the vendored `pricingIntelligence.ts` header):
 * the SERVER owns the comparable pool; the CLIENT owns the subject's position
 * within it. Selector changes re-run the brief (debounced 400 ms, request
 * sequenced, stale responses dropped); asking-price changes recompute locally
 * through `positionFor` and NEVER touch the network.
 *
 * The asking price IS included in the request body so the server reports the
 * FX-conversion rate age for LBP subjects — but it is deliberately excluded
 * from the effect dependencies below: moving the slider must not re-fetch.
 *
 * LBP note (spec §8): the session rate is not available client-side, so the
 * page derives USD-per-LBP from the fetched `target.amount / subject price`
 * and reuses it for live marker math. Its age rides in the amber badge.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@/api/client'
import { Numeric } from '@/components/ui/numeric'
import { useLocale } from '@/hooks/useLocale'
import { usePageTitle } from '@/lib/usePageTitle'
import type {
  PricingTrendSnapshot,
  SubjectAnalysis,
  SubjectAnalysisRequest,
  SubjectComparable,
} from '@/types/marketPricing'
import { HonestStat } from './HonestStat'
import { PriceLadder } from './PriceLadder'
import { PriceHistoryLine } from './PriceHistoryLine'
import { QuarterBenchmark } from './QuarterBenchmark'
import { WeightedDotStrip } from './WeightedDotStrip'
import { formatMoney, positionFor, priceDomain } from './pricingIntelligence'
import {
  formatObservedDate,
  ordinalForm,
  priceBriefT,
  propertyTypeLabel,
  type PriceBriefLocale,
} from './copy'

const DEBOUNCE_MS = 400

type Phase = 'idle' | 'loading' | 'ready' | 'insufficient' | 'error'
type Currency = 'USD' | 'LBP'

type ErrorInfo =
  | { kind: 'rate' }
  | { kind: 'upsell'; upsellUrl: string | null }
  | { kind: 'unknown'; message: string }

function classifyError(err: unknown): ErrorInfo {
  const e = err as {
    status?: number
    code?: string
    error?: string
    message?: string
    upsell_url?: string
  }
  // §10.2 entitlement gate (PR 579) — same shape as FEATURE_NOT_ENABLED.
  if (
    e.status === 402 ||
    e.status === 403 ||
    e.code === 'FEATURE_NOT_ENABLED' ||
    e.error === 'FEATURE_NOT_ENABLED'
  ) {
    return { kind: 'upsell', upsellUrl: e.upsell_url ?? null }
  }
  if (e.status === 503 || e.code === 'CURRENCY_RATE_UNAVAILABLE') return { kind: 'rate' }
  return { kind: 'unknown', message: e.message || 'Request failed' }
}

/** VALIDATION_ERROR.fields (400) mapped field-by-field onto the panel. */
function validationFields(err: unknown): Record<string, string[]> | null {
  const e = err as { status?: number; code?: string; fields?: Record<string, string[]> }
  if (e.status === 400 && e.fields && typeof e.fields === 'object') return e.fields
  if (e.code === 'VALIDATION_ERROR' && e.fields && typeof e.fields === 'object') return e.fields
  return null
}

function parseOptionalNumber(text: string): number | null {
  if (text.trim() === '') return null
  const n = Number(text)
  return Number.isFinite(n) && n > 0 ? n : null
}

export function PriceBriefPage() {
  const { locale, dir } = useLocale()
  const t = (key: Parameters<typeof priceBriefT>[0], vars?: Record<string, string | number>) =>
    priceBriefT(key, locale, vars)
  usePageTitle(t('pageTitle'))

  // Selectors — changing these re-runs the whole brief (spec §7).
  const [propertyType, setPropertyType] = useState('apartment')
  const [transactionType, setTransactionType] = useState<'sale' | 'rent'>('sale')
  const [location, setLocation] = useState('')
  // Comparators — refines the weighting, never the pool.
  const [areaText, setAreaText] = useState('')
  const [bedroomsText, setBedroomsText] = useState('')
  const [bathroomsText, setBathroomsText] = useState('')

  // The asking price lives OUTSIDE the panel, in the verdict section (§7).
  const [priceText, setPriceText] = useState('')
  const [currency, setCurrency] = useState<Currency>('USD')

  const [phase, setPhase] = useState<Phase>('idle')
  const [analysis, setAnalysis] = useState<SubjectAnalysis | null>(null)
  const [errorInfo, setErrorInfo] = useState<ErrorInfo | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})
  const [asOf, setAsOf] = useState<string | null>(null)
  const [panelOpen, setPanelOpen] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  /** USD-per-LBP derived from the fetched target, for live LBP marker math. */
  const [lbpConversion, setLbpConversion] = useState<number | null>(null)

  const requestSeq = useRef(0)
  const forceImmediateRef = useRef(true)
  /** Last SETTLED phase ('loading' excluded) — drives panel auto-close and validation restore. */
  const lastSettledRef = useRef<Phase>('idle')
  const [runNonce, setRunNonce] = useState(0)
  /** Area price history. Independent of the pool: it describes the market,
   *  not the subject, so a failure here must never disturb the brief. */
  const [trends, setTrends] = useState<PricingTrendSnapshot[]>([])

  const price = useMemo(() => parseOptionalNumber(priceText), [priceText])
  const canRun = location.trim().length > 0

  const areaId = analysis?.area?.id ?? null
  useEffect(() => {
    if (!areaId) {
      setTrends([])
      return
    }
    let cancelled = false
    void api
      .getPricingTrends(areaId, propertyType)
      .then((rows) => {
        if (!cancelled) setTrends(Array.isArray(rows) ? rows : [])
      })
      // No history is a normal state for a young area, not an error worth
      // putting in front of an agent mid-conversation.
      .catch(() => {
        if (!cancelled) setTrends([])
      })
    return () => {
      cancelled = true
    }
  }, [areaId, propertyType])

  useEffect(() => {
    if (!canRun) {
      requestSeq.current += 1
      forceImmediateRef.current = true
      setPhase('idle')
      setAnalysis(null)
      setErrorInfo(null)
      setFieldErrors({})
      return
    }
    const delay = forceImmediateRef.current ? 0 : DEBOUNCE_MS
    forceImmediateRef.current = false
    const timer = setTimeout(() => {
      const seq = ++requestSeq.current
      setPhase('loading')
      setErrorInfo(null)
      void (async () => {
        try {
          const body: SubjectAnalysisRequest = {
            property_type: propertyType,
            transaction_type: transactionType,
            city: location.trim(),
            area: parseOptionalNumber(areaText),
            bedrooms: parseOptionalNumber(bedroomsText),
            bathrooms: parseOptionalNumber(bathroomsText),
            ...(price != null ? { price, currency } : {}),
          }
          const result = await api.analyseSubject(body)
          if (seq !== requestSeq.current) return
          setAsOf(new Date().toISOString())
          setSelectedId(null)
          setAnalysis(result)
          setFieldErrors({})
          setLbpConversion(
            currency === 'LBP' && price != null && result.target?.amount != null
              ? result.target.amount / price
              : null,
          )
          setPhase(result.sufficient === false ? 'insufficient' : 'ready')
        } catch (err) {
          if (seq !== requestSeq.current) return
          const fields = validationFields(err)
          if (fields) {
            // Invalid NEW subject: keep whatever brief is on screen, flag the
            // offending fields inline and open the panel (spec §7). Restore
            // the phase the brief had before this request started loading.
            setFieldErrors(fields)
            setPanelOpen(true)
            setPhase(lastSettledRef.current)
            return
          }
          setErrorInfo(classifyError(err))
          setPhase('error')
        }
      })()
    }, delay)
    return () => clearTimeout(timer)
    // `price`/`priceText` are deliberately NOT dependencies: moving the asking
    // price recomputes locally and must never hit the network (spec §11.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    canRun,
    propertyType,
    transactionType,
    location,
    areaText,
    bedroomsText,
    bathroomsText,
    currency,
    runNonce,
  ])

  // Collapsed by default once a result exists; opened by default when none.
  useEffect(() => {
    if (phase === 'loading') return
    const prev = lastSettledRef.current
    if (phase === 'ready' && (prev === 'idle' || prev === 'insufficient' || prev === 'error')) {
      setPanelOpen(false)
    }
    lastSettledRef.current = phase
  }, [phase])

  function retry() {
    forceImmediateRef.current = true
    setRunNonce((n) => n + 1)
  }

  const distribution = analysis?.sufficient ? (analysis.distribution ?? null) : null
  const comparables = analysis?.comparables ?? []

  const priceNormalized = useMemo(() => {
    if (price == null) return null
    if (currency === 'USD') return price
    return lbpConversion != null ? price * lbpConversion : null
  }, [price, currency, lbpConversion])

  const live =
    analysis && distribution && priceNormalized != null
      ? positionFor(analysis, priceNormalized)
      : null
  const domain = distribution ? priceDomain(distribution, priceNormalized) : null

  const rateStale =
    analysis?.target != null &&
    (analysis.target.rate_is_stale || (analysis.target.rate_age_hours ?? 0) > 48)
  const rateAgeDays =
    analysis?.target?.rate_age_hours != null
      ? Math.max(1, Math.floor(analysis.target.rate_age_hours / 24))
      : null

  const areaSqm = parseOptionalNumber(areaText)
  const selected = selectedId ? comparables.find((c) => c.id === selectedId) : undefined

  return (
    <div
      className="min-h-screen bg-[var(--lc-bg-page)] px-[var(--lc-space-md)] py-[var(--lc-space-lg)] sm:px-[var(--lc-space-xl)]"
      dir={dir}
      lang={locale}
      data-testid="price-brief-page"
    >
      <div className="mx-auto max-w-[960px] space-y-[var(--lc-space-xl)]">
        {phase === 'idle' && <EmptyBrief t={t} />}

        {phase === 'loading' && <LoadingBrief t={t} />}

        {phase === 'error' && errorInfo && (
          <ErrorBrief info={errorInfo} t={t} onRetry={retry} />
        )}

        {(phase === 'ready' || phase === 'insufficient') && analysis && (
          <>
            <SubjectStrip
              t={t}
              locale={locale}
              propertyType={propertyType}
              transactionType={transactionType}
              location={location}
              areaName={analysis.area?.name ?? null}
              areaSqm={areaSqm}
              analysis={analysis}
              asOf={asOf}
              rateStale={rateStale}
              rateAgeDays={rateAgeDays}
            />

            {phase === 'insufficient' ? (
              <InsufficientBrief reason={analysis.reason ?? ''} t={t} />
            ) : (
              distribution && domain && (
                <VerdictSection
                  t={t}
                  locale={locale}
                  dir={dir}
                  distribution={distribution}
                  domain={domain}
                  analysis={analysis}
                  live={live}
                  price={price}
                  priceNormalized={priceNormalized}
                  priceText={priceText}
                  setPriceText={setPriceText}
                  currency={currency}
                  setCurrency={setCurrency}
                  lbpConversion={lbpConversion}
                />
              )
            )}

            {phase === 'ready' && comparables.length > 0 && domain && (
              <EvidenceSection
                t={t}
                locale={locale}
                dir={dir}
                analysis={analysis}
                domain={domain}
                selected={selected}
                selectedId={selectedId}
                setSelectedId={setSelectedId}
              />
            )}

            {analysis.area_market && (
              <AreaAndPoolPanels t={t} market={analysis.area_market} total={analysis.comparable_count} />
            )}
          </>
        )}

        {(phase === 'ready' || phase === 'insufficient' || phase === 'error' || phase === 'idle' || phase === 'loading') && (
          <SubjectPanel
            t={t}
            locale={locale}
            open={panelOpen}
            setOpen={setPanelOpen}
            fieldErrors={fieldErrors}
            {...panelProps()}
          />
        )}

        {phase === 'ready' && analysis?.sufficient ? (
          <section
            data-testid="history-section"
            aria-label={t('historyEyebrow')}
            className="space-y-[var(--lc-space-lg)] rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-[var(--lc-space-md)]"
          >
            <div>
              <p className="m-0 mb-[var(--lc-space-2xs)] text-[length:var(--lc-type-overline)] uppercase tracking-[var(--lc-tracking-overline)] text-[var(--lc-text-muted)]">
                {t('historyEyebrow')}
              </p>
              <PriceHistoryLine
                snapshots={trends}
                currency={analysis.currency ?? 'USD'}
                dir={dir}
                locale={locale}
              />
            </div>
            <div>
              <p className="m-0 mb-[var(--lc-space-2xs)] text-[length:var(--lc-type-overline)] uppercase tracking-[var(--lc-tracking-overline)] text-[var(--lc-text-muted)]">
                {t('benchmarkEyebrow')}
              </p>
              <QuarterBenchmark
                comparables={analysis.comparables}
                currency={analysis.currency ?? 'USD'}
                dir={dir}
                locale={locale}
              />
            </div>
          </section>
        ) : (
          <HistorySlot t={t} />
        )}
      </div>
    </div>
  )

  function panelProps() {
    return {
      propertyType,
      setPropertyType,
      transactionType,
      setTransactionType,
      location,
      setLocation,
      areaText,
      setAreaText,
      bedroomsText,
      setBedroomsText,
      bathroomsText,
      setBathroomsText,
    }
  }
}

type T = (key: Parameters<typeof priceBriefT>[0], vars?: Record<string, string | number>) => string

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

function SubjectStrip({
  t,
  locale,
  propertyType,
  transactionType,
  location,
  areaName,
  areaSqm,
  analysis,
  asOf,
  rateStale,
  rateAgeDays,
}: {
  t: T
  locale: PriceBriefLocale
  propertyType: string
  transactionType: 'sale' | 'rent'
  location: string
  areaName: string | null
  areaSqm: number | null
  analysis: SubjectAnalysis
  asOf: string | null
  rateStale: boolean
  rateAgeDays: number | null
}) {
  const confidenceKey =
    analysis.confidence === 'high'
      ? ('confidenceHigh' as const)
      : analysis.confidence === 'low'
        ? ('confidenceLow' as const)
        : ('confidenceMedium' as const)
  return (
    <header
      className="flex flex-wrap items-center gap-x-[var(--lc-space-md)] gap-y-[var(--lc-space-xs)] border-b border-[var(--lc-border)] pb-[var(--lc-space-md)]"
      data-testid="subject-strip"
    >
      <p className="m-0 text-[length:var(--lc-type-body-lg)] text-[var(--lc-text-primary)]">
        <strong className="text-[var(--lc-text-heading)]">
          {propertyTypeLabel(propertyType, locale)}
        </strong>
        {' · '}
        {transactionType === 'sale' ? t('fieldTransactionSale') : t('fieldTransactionRent')}
        {' · '}
        {areaName ?? location}
        {areaSqm != null && (
          <>
            {' · '}
            <Numeric>{areaSqm}</Numeric> m²
          </>
        )}
      </p>
      <p className="m-0 flex flex-wrap items-center gap-x-[var(--lc-space-md)] gap-y-[var(--lc-space-2xs)] text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
        <span>
          {t('confidenceLabel')}:{' '}
          <strong className="text-[var(--lc-text-secondary)]">{t(confidenceKey)}</strong>
          {analysis.effective_sample_size != null && (
            <>
              {' · '}
              {t('effectiveSample', { value: analysis.effective_sample_size })}
            </>
          )}
        </span>
        {asOf && <span>{t('asOf', { date: formatObservedDate(asOf) })}</span>}
        {rateStale ? (
          <span
            className="rounded-[var(--lc-radius-sm)] bg-[var(--lc-status-underOffer-bg)] px-[var(--lc-space-xs)] py-[2px] font-medium text-[var(--lc-status-underOffer-fg)]"
            data-testid="rate-stale-badge"
          >
            {t('rateStale', { age: `${rateAgeDays ?? 2} days` })}
          </span>
        ) : (
          analysis.target != null && (
            <span className="rounded-[var(--lc-radius-sm)] bg-[var(--lc-status-published-bg)] px-[var(--lc-space-xs)] py-[2px] font-medium text-[var(--lc-status-published-fg)]">
              {t('fxFresh')}
            </span>
          )
        )}
      </p>
    </header>
  )
}

function VerdictSection({
  t,
  locale,
  dir,
  distribution,
  domain,
  analysis,
  live,
  price,
  priceNormalized,
  priceText,
  setPriceText,
  currency,
  setCurrency,
  lbpConversion,
}: {
  t: T
  locale: PriceBriefLocale
  dir: 'ltr' | 'rtl'
  distribution: NonNullable<SubjectAnalysis['distribution']>
  domain: { min: number; max: number }
  analysis: SubjectAnalysis
  live: ReturnType<typeof positionFor> | null
  price: number | null
  priceNormalized: number | null
  priceText: string
  setPriceText: (v: string) => void
  currency: Currency
  setCurrency: (v: Currency) => void
  lbpConversion: number | null
}) {
  const hasPrice = price != null && priceNormalized != null
  const conversion = currency === 'LBP' ? lbpConversion : 1
  // The slider works in the SUBJECT's currency; the ladder works in USD.
  const sliderMin = conversion ? domain.min / conversion : domain.min
  const sliderMax = conversion ? domain.max / conversion : domain.max
  const sliderStep = Math.max(1, Math.round((sliderMax - sliderMin) / 200))
  const sliderValue =
    price ?? Math.round(conversion ? distribution.median / conversion : distribution.median)
  return (
    <section data-testid="verdict-section" aria-label={t('verdictEyebrow')}>
      <p className="m-0 mb-[var(--lc-space-2xs)] text-[length:var(--lc-type-overline)] uppercase tracking-[var(--lc-tracking-overline)] text-[var(--lc-text-muted)]">
        {t('verdictEyebrow')}
      </p>
      {hasPrice && live ? (
        <h1 className="m-0 text-[length:var(--lc-type-display)] tracking-[var(--lc-tracking-display)] text-[var(--lc-text-heading)]">
          {t('percentileReadout', { ordinal: ordinalForm(Math.round(live.percentile ?? 0), locale) })}
        </h1>
      ) : (
        <h1 className="m-0 text-[length:var(--lc-type-display)] tracking-[var(--lc-tracking-display)] text-[var(--lc-text-heading)]">
          {t('priceRange', {
            low: formatMoney(distribution.p25, 'USD'),
            high: formatMoney(distribution.p75, 'USD'),
          })}
        </h1>
      )}
      <p className="m-0 mt-[var(--lc-space-2xs)] text-[length:var(--lc-type-body)] text-[var(--lc-text-secondary)]">
        {t('shouldBePriced')}
      </p>

      <div className="mt-[var(--lc-space-md)] rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-[var(--lc-space-md)]">
        <PriceLadder
          distribution={distribution}
          price={priceNormalized}
          currency="USD"
          dir={dir}
          locale={locale}
        />
        <p className="m-0 mt-[var(--lc-space-xs)] text-center text-[length:var(--lc-type-body)] text-[var(--lc-text-secondary)]" data-testid="verdict-readout">
          {live && hasPrice ? (
            live.position === 'above' ? (
              t('readoutAbove', { pct: Math.abs(Math.round(live.vsMedianPercent)), count: analysis.comparable_count })
            ) : live.position === 'below' ? (
              t('readoutBelow', { pct: Math.abs(Math.round(live.vsMedianPercent)), count: analysis.comparable_count })
            ) : (
              t('readoutAt', { count: analysis.comparable_count })
            )
          ) : (
            t('readoutNoPrice', { count: analysis.comparable_count })
          )}
        </p>

        <div className="mt-[var(--lc-space-md)] flex flex-wrap items-center gap-[var(--lc-space-sm)]">
          <label htmlFor="pb-price" className="text-[length:var(--lc-type-body-sm)] font-medium text-[var(--lc-text-secondary)]">
            {t('priceLabel')}
          </label>
          <input
            id="pb-price"
            type="range"
            min={Math.round(sliderMin)}
            max={Math.round(sliderMax)}
            step={sliderStep}
            value={Math.round(sliderValue)}
            onChange={(e) => setPriceText(String(Math.round(Number(e.target.value))))}
            className="min-w-[180px] flex-1 accent-[var(--lc-action-primary)]"
            aria-label={t('priceLabel')}
            data-testid="price-slider"
          />
          <input
            type="number"
            inputMode="numeric"
            min={1}
            value={priceText}
            placeholder={t('pricePlaceholder')}
            onChange={(e) => setPriceText(e.target.value)}
            className="w-[140px] rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-[var(--lc-space-sm)] py-[var(--lc-space-2xs)] font-[family-name:var(--lc-font-mono)] text-[var(--lc-text-primary)]"
            data-testid="price-input"
            aria-label={t('priceLabel')}
          />
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value as Currency)}
            className="rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-[var(--lc-space-sm)] py-[var(--lc-space-2xs)] text-[var(--lc-text-primary)]"
            aria-label={t('priceLabel')}
            data-testid="currency-select"
          >
            <option value="USD">{t('currencyUsd')}</option>
            <option value="LBP">{t('currencyLbp')}</option>
          </select>
        </div>
        {currency === 'LBP' && (
          <p className="m-0 mt-[var(--lc-space-xs)] text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
            {t('lbpNote')}
          </p>
        )}
      </div>
    </section>
  )
}

function EvidenceSection({
  t,
  locale,
  dir,
  analysis,
  domain,
  selected,
  selectedId,
  setSelectedId,
}: {
  t: T
  locale: PriceBriefLocale
  dir: 'ltr' | 'rtl'
  analysis: SubjectAnalysis
  domain: { min: number; max: number }
  selected: SubjectComparable | undefined
  selectedId: string | null
  setSelectedId: (id: string | null) => void
}) {
  return (
    <section data-testid="evidence-section" aria-label={t('evidenceEyebrow')}>
      <p className="m-0 mb-[var(--lc-space-2xs)] text-[length:var(--lc-type-overline)] uppercase tracking-[var(--lc-tracking-overline)] text-[var(--lc-text-muted)]">
        {t('evidenceEyebrow')}
      </p>
      <p className="m-0 mb-[var(--lc-space-xs)] text-[length:var(--lc-type-body)] text-[var(--lc-text-secondary)]">
        {t('evidenceCount', { count: analysis.comparable_count })}
      </p>
      <div className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-[var(--lc-space-md)]">
        <WeightedDotStrip
          comparables={analysis.comparables}
          domain={domain}
          currency="USD"
          dir={dir}
          locale={locale}
          selectedId={selectedId}
          onSelect={setSelectedId}
        />
        {selected && (
          <EvidenceCard
            comparable={selected}
            t={t}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>
    </section>
  )
}

function EvidenceCard({
  comparable: c,
  t,
  onClose,
}: {
  comparable: SubjectComparable
  t: T
  onClose: () => void
}) {
  const sold = Boolean(c.is_closed)
  return (
    <div
      className="mt-[var(--lc-space-md)] rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface-raised)] p-[var(--lc-space-md)]"
      data-testid="evidence-card"
    >
      <div className="flex flex-wrap items-center gap-[var(--lc-space-sm)]">
        <span
          className={`rounded-[var(--lc-radius-pill)] px-[var(--lc-space-sm)] py-[2px] text-[length:var(--lc-type-caption)] font-semibold ${
            sold
              ? 'bg-[var(--lc-status-published-bg)] text-[var(--lc-status-published-fg)]'
              : 'bg-[var(--lc-status-draft-bg)] text-[var(--lc-status-draft-fg)]'
          }`}
          data-testid="evidence-card-kind"
        >
          {sold ? t('kindVerifiedSale') : t('kindAsking')}
        </span>
        <strong className="text-[length:var(--lc-type-heading-3)] text-[var(--lc-text-heading)]">
          {formatMoney(c.normalized_price, 'USD')}
        </strong>
        <span className="text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-muted)]">
          {c.source_label || c.source}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="ms-auto rounded-[var(--lc-radius-sm)] border border-[var(--lc-border)] px-[var(--lc-space-sm)] py-[2px] text-[length:var(--lc-type-caption)] text-[var(--lc-text-secondary)]"
        >
          {t('cardClose')}
        </button>
      </div>
      <p className="m-0 mt-[var(--lc-space-xs)] flex flex-wrap gap-x-[var(--lc-space-md)] gap-y-[var(--lc-space-2xs)] text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-secondary)]">
        {c.price_per_sqm != null && <span>{t('cardPerSqm', { value: formatMoney(c.price_per_sqm, 'USD') })}</span>}
        {c.area_sqm != null && <span>{t('cardSize', { value: c.area_sqm })}</span>}
        {c.bedrooms != null && c.bathrooms != null && (
          <span>{t('cardBedsBaths', { beds: c.bedrooms, baths: c.bathrooms })}</span>
        )}
        {c.similarity_score != null && (
          <span>{t('cardSimilarity', { pct: Math.round(c.similarity_score * 100) })}</span>
        )}
        {c.days_since_listed != null && (
          <span>{t('cardDaysListed', { days: c.days_since_listed })}</span>
        )}
        <strong className="text-[var(--lc-text-primary)]">
          {sold
            ? t('observedSold', { date: formatObservedDate(c.observed_at) })
            : t('observedFirstSeen', { date: formatObservedDate(c.observed_at) })}
        </strong>
      </p>
    </div>
  )
}

function AreaAndPoolPanels({
  t,
  market,
  total,
}: {
  t: T
  market: NonNullable<SubjectAnalysis['area_market']>
  total: number
}) {
  const { active, sold, alignment } = market
  const segments = [
    { key: 'strong', count: alignment.strong, label: t('poolStrong'), bg: 'var(--lc-status-published-bg)', fg: 'var(--lc-status-published-fg)' },
    { key: 'moderate', count: alignment.moderate, label: t('poolModerate'), bg: 'var(--lc-status-underOffer-bg)', fg: 'var(--lc-status-underOffer-fg)' },
    { key: 'weak', count: alignment.weak, label: t('poolWeak'), bg: 'var(--lc-status-draft-bg)', fg: 'var(--lc-status-draft-fg)' },
  ]
  return (
    <div className="grid gap-[var(--lc-space-lg)] md:grid-cols-2">
      <section
        data-testid="area-panel"
        aria-label={t('areaEyebrow')}
        className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-[var(--lc-space-md)]"
      >
        <h2 className="m-0 mb-[var(--lc-space-md)] text-[length:var(--lc-type-heading-3)] text-[var(--lc-text-heading)]">
          {t('areaEyebrow')}
        </h2>
        <div className="flex flex-col gap-[var(--lc-space-lg)]">
          <HonestStat
            testId="stat-active"
            label={t('activeLabel')}
            value={active.listing_count}
            basis={`${active.listing_count} ${t('activeBasis')}`}
          />
          <HonestStat
            testId="stat-days"
            label={t('daysLabel')}
            value={active.median_days_listed != null ? `${active.median_days_listed}d` : '—'}
            basis={
              active.median_days_listed != null
                ? t('daysBasis', { days: active.median_days_listed })
                : '—'
            }
          />
          <HonestStat
            testId="stat-sold"
            label={t('soldLabel')}
            value={sold.sold_count}
            basis={`${t('soldBasis', { count: sold.sold_count, window: sold.window_days })}${
              sold.median_sold_price != null ? `, ${t('soldMedian', { value: formatMoney(sold.median_sold_price, 'USD') })}` : ''
            }`}
          />
        </div>
      </section>

      <section
        data-testid="pool-panel"
        aria-label={t('poolEyebrow')}
        className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-[var(--lc-space-md)]"
      >
        <h2 className="m-0 mb-[var(--lc-space-md)] text-[length:var(--lc-type-heading-3)] text-[var(--lc-text-heading)]">
          {t('poolEyebrow')}
        </h2>
        <div
          className="flex h-[var(--lc-space-xl)] w-full overflow-hidden rounded-[var(--lc-radius-sm)]"
          role="img"
          aria-label={segments.map((s) => `${s.label}: ${s.count}`).join(', ')}
          data-testid="pool-stacked-bar"
        >
          {segments.map((s) =>
            s.count > 0 ? (
              <div
                key={s.key}
                style={{ width: `${(s.count / Math.max(1, alignment.total)) * 100}%`, background: s.bg, color: s.fg }}
                className="flex items-center justify-center text-[length:var(--lc-type-caption)] font-semibold"
                data-testid={`pool-segment-${s.key}`}
              >
                {s.count}
              </div>
            ) : null,
          )}
        </div>
        <ul className="m-0 mt-[var(--lc-space-xs)] flex list-none flex-wrap gap-x-[var(--lc-space-md)] p-0 text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
          {segments.map((s) => (
            <li key={s.key} className="flex items-center gap-[var(--lc-space-2xs)]">
              <span
                className="inline-block h-[10px] w-[10px] rounded-[2px]"
                style={{ background: s.bg }}
                aria-hidden="true"
              />
              {s.label} · <Numeric>{s.count}</Numeric>
            </li>
          ))}
        </ul>
        <div className="mt-[var(--lc-space-md)] flex flex-col gap-[var(--lc-space-2xs)]">
          <span className="text-[length:var(--lc-type-caption)] uppercase tracking-[var(--lc-tracking-overline)] text-[var(--lc-text-muted)]">
            {t('poolMedianSimilarity')}
          </span>
          <Numeric className="text-[length:var(--lc-type-heading-3)] text-[var(--lc-text-heading)]">
            {alignment.median_similarity != null ? alignment.median_similarity.toFixed(2) : '—'}
          </Numeric>
          <span className="text-[length:var(--lc-type-body-sm)] text-[var(--lc-text-muted)]">
            {t('poolPullLine', { strong: alignment.strong, total })}
          </span>
        </div>
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Subject panel (the form — demoted but not hidden, spec §7)
// ---------------------------------------------------------------------------

interface PanelFieldProps {
  propertyType: string
  setPropertyType: (v: string) => void
  transactionType: 'sale' | 'rent'
  setTransactionType: (v: 'sale' | 'rent') => void
  location: string
  setLocation: (v: string) => void
  areaText: string
  setAreaText: (v: string) => void
  bedroomsText: string
  setBedroomsText: (v: string) => void
  bathroomsText: string
  setBathroomsText: (v: string) => void
}

function SubjectPanel({
  t,
  locale,
  open,
  setOpen,
  fieldErrors,
  ...fields
}: PanelFieldProps & { t: T; locale: PriceBriefLocale; open: boolean; setOpen: (v: boolean) => void; fieldErrors: Record<string, string[]> }) {
  const locationError =
    fieldErrors.location?.[0] ?? fieldErrors.city?.[0] ?? fieldErrors.neighborhood?.[0]
  const fieldError = (key: string) => fieldErrors[key]?.[0]
  return (
    <section className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)]">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls="pb-subject-panel"
        className="flex w-full items-center justify-between px-[var(--lc-space-md)] py-[var(--lc-space-sm)] text-start"
        data-testid="panel-toggle"
      >
        <span className="text-[length:var(--lc-type-heading-3)] text-[var(--lc-text-heading)]">
          {open ? '▾' : '▸'} {t('panelTitle')}
        </span>
      </button>
      {(
        <div
          id="pb-subject-panel"
          hidden={!open}
          className="border-t border-[var(--lc-border)] p-[var(--lc-space-md)]"
        >
          <fieldset className="m-0 border-0 p-0">
            <legend className="mb-[var(--lc-space-xs)] text-[length:var(--lc-type-caption)] uppercase tracking-[var(--lc-tracking-overline)] text-[var(--lc-text-muted)]">
              {t('panelSelectors')}
            </legend>
            <div className="grid gap-[var(--lc-space-md)] sm:grid-cols-3">
              <Field label={t('fieldPropertyType')} htmlFor="pb-property-type" error={fieldError('property_type')}>
                <select
                  id="pb-property-type"
                  value={fields.propertyType}
                  onChange={(e) => fields.setPropertyType(e.target.value)}
                  className="w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-[var(--lc-space-sm)] py-[var(--lc-space-2xs)] text-[var(--lc-text-primary)]"
                >
                  {['apartment', 'villa', 'townhouse', 'studio', 'penthouse', 'office', 'shop'].map((pt) => (
                    <option key={pt} value={pt}>
                      {propertyTypeLabel(pt, locale)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t('fieldTransactionType')} htmlFor="pb-transaction-type" error={fieldError('transaction_type')}>
                <select
                  id="pb-transaction-type"
                  value={fields.transactionType}
                  onChange={(e) => fields.setTransactionType(e.target.value as 'sale' | 'rent')}
                  className="w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-[var(--lc-space-sm)] py-[var(--lc-space-2xs)] text-[var(--lc-text-primary)]"
                >
                  <option value="sale">{t('fieldTransactionSale')}</option>
                  <option value="rent">{t('fieldTransactionRent')}</option>
                </select>
              </Field>
              <Field label={t('fieldLocation')} htmlFor="pb-location" error={locationError}>
                <input
                  id="pb-location"
                  type="text"
                  value={fields.location}
                  placeholder={t('fieldLocationPlaceholder')}
                  onChange={(e) => fields.setLocation(e.target.value)}
                  className="w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-[var(--lc-space-sm)] py-[var(--lc-space-2xs)] text-[var(--lc-text-primary)]"
                />
              </Field>
            </div>
          </fieldset>

          <fieldset className="m-0 mt-[var(--lc-space-lg)] border-0 p-0">
            <legend className="mb-[var(--lc-space-xs)] text-[length:var(--lc-type-caption)] uppercase tracking-[var(--lc-tracking-overline)] text-[var(--lc-text-muted)]">
              {t('panelComparators')}
            </legend>
            <div className="grid gap-[var(--lc-space-md)] sm:grid-cols-3">
              <Field label={t('fieldArea')} htmlFor="pb-area" error={fieldError('area')}>
                <input
                  id="pb-area"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={fields.areaText}
                  onChange={(e) => fields.setAreaText(e.target.value)}
                  className="w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-[var(--lc-space-sm)] py-[var(--lc-space-2xs)] font-[family-name:var(--lc-font-mono)] text-[var(--lc-text-primary)]"
                />
              </Field>
              <Field label={t('fieldBedrooms')} htmlFor="pb-bedrooms" error={fieldError('bedrooms')}>
                <input
                  id="pb-bedrooms"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={fields.bedroomsText}
                  onChange={(e) => fields.setBedroomsText(e.target.value)}
                  className="w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-[var(--lc-space-sm)] py-[var(--lc-space-2xs)] font-[family-name:var(--lc-font-mono)] text-[var(--lc-text-primary)]"
                />
              </Field>
              <Field label={t('fieldBathrooms')} htmlFor="pb-bathrooms" error={fieldError('bathrooms')}>
                <input
                  id="pb-bathrooms"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={fields.bathroomsText}
                  onChange={(e) => fields.setBathroomsText(e.target.value)}
                  className="w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-[var(--lc-space-sm)] py-[var(--lc-space-2xs)] font-[family-name:var(--lc-font-mono)] text-[var(--lc-text-primary)]"
                />
              </Field>
            </div>
          </fieldset>
        </div>
      )}
    </section>
  )
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string
  htmlFor: string
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-[var(--lc-space-2xs)]">
      <label htmlFor={htmlFor} className="text-[length:var(--lc-type-body-sm)] font-medium text-[var(--lc-text-secondary)]">
        {label}
      </label>
      {children}
      {error && (
        <p role="alert" className="m-0 text-[length:var(--lc-type-caption)] text-[var(--lc-status-unpublished-fg)]">
          {error}
        </p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Designed states (spec §8)
// ---------------------------------------------------------------------------

function EmptyBrief({ t }: { t: T }) {
  return (
    <div data-testid="empty-state" className="space-y-[var(--lc-space-xl)]">
      <div className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-[var(--lc-space-xl)] text-center">
        <h1 className="m-0 text-[length:var(--lc-type-display)] tracking-[var(--lc-tracking-display)] text-[var(--lc-text-heading)]">
          {t('emptyTitle')}
        </h1>
        <p className="m-0 mt-[var(--lc-space-sm)] text-[length:var(--lc-type-body)] text-[var(--lc-text-secondary)]">
          {t('emptyBody')}
        </p>
      </div>
    </div>
  )
}

function LoadingBrief({ t }: { t: T }) {
  const bar = 'rounded-[var(--lc-radius-sm)] bg-[var(--lc-surface-sunken)]'
  return (
    <div data-testid="loading-state" aria-label={t('loadingAria')} aria-busy="true" className="space-y-[var(--lc-space-xl)]">
      <div className={`h-[var(--lc-space-2xl)] w-2/3 ${bar}`} />
      <div className={`h-[var(--lc-space-5xl)] w-full ${bar}`} />
      <div className={`h-[var(--lc-space-4xl)] w-full ${bar}`} />
      <div className="grid gap-[var(--lc-space-lg)] md:grid-cols-2">
        <div className={`h-[var(--lc-space-5xl)] w-full ${bar}`} />
        <div className={`h-[var(--lc-space-5xl)] w-full ${bar}`} />
      </div>
      <div className={`h-[var(--lc-space-4xl)] w-full ${bar}`} />
    </div>
  )
}

function ErrorBrief({ info, t, onRetry }: { info: ErrorInfo; t: T; onRetry: () => void }) {
  if (info.kind === 'upsell') {
    return (
      <div
        data-testid="upsell-state"
        className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-[var(--lc-space-xl)] text-center"
      >
        <h1 className="m-0 text-[length:var(--lc-type-heading-1)] text-[var(--lc-text-heading)]">
          {t('upsellTitle')}
        </h1>
        <p className="m-0 mt-[var(--lc-space-sm)] text-[length:var(--lc-type-body)] text-[var(--lc-text-secondary)]">
          {t('upsellBody')}
        </p>
        {info.upsellUrl && (
          <a
            href={info.upsellUrl}
            className="mt-[var(--lc-space-md)] inline-block rounded-[var(--lc-radius-md)] bg-[var(--lc-action-primary)] px-[var(--lc-space-lg)] py-[var(--lc-space-sm)] font-semibold text-[var(--lc-action-primary-text)]"
          >
            {t('upsellCta')}
          </a>
        )}
      </div>
    )
  }
  const rate = info.kind === 'rate'
  return (
    <div
      data-testid={rate ? 'rate-error-state' : 'generic-error-state'}
      className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-[var(--lc-space-xl)] text-center"
    >
      <h1 className="m-0 text-[length:var(--lc-type-heading-1)] text-[var(--lc-text-heading)]">
        {rate ? t('errorRateTitle') : t('errorGenericTitle')}
      </h1>
      {rate && (
        <p className="m-0 mt-[var(--lc-space-sm)] text-[length:var(--lc-type-body)] text-[var(--lc-text-secondary)]">
          {t('errorRateBody')}
        </p>
      )}
      <button
        type="button"
        onClick={onRetry}
        className="mt-[var(--lc-space-md)] rounded-[var(--lc-radius-md)] bg-[var(--lc-action-primary)] px-[var(--lc-space-lg)] py-[var(--lc-space-sm)] font-semibold text-[var(--lc-action-primary-text)]"
      >
        {t('errorRetry')}
      </button>
    </div>
  )
}

function InsufficientBrief({
  reason,
  t,
}: {
  reason: string
  t: T
}) {
  // The engine's two refusal shapes carry their own fix; pick by content so
  // the advice stays verbatim-honest (spec §8).
  const needsArea = /coordinates|known area/i.test(reason)
  return (
    <section
      data-testid="insufficient-state"
      className="rounded-[var(--lc-radius-lg)] border border-[var(--lc-border)] bg-[var(--lc-surface)] p-[var(--lc-space-xl)]"
    >
      <p className="m-0 mb-[var(--lc-space-2xs)] text-[length:var(--lc-type-overline)] uppercase tracking-[var(--lc-tracking-overline)] text-[var(--lc-text-muted)]">
        {t('insufficientEyebrow')}
      </p>
      <h1 className="m-0 text-[length:var(--lc-type-heading-1)] text-[var(--lc-text-heading)]">
        {t('insufficientTitle')}
      </h1>
      <blockquote className="m-0 mt-[var(--lc-space-md)] border-s-[3px] border-[var(--lc-border-strong)] ps-[var(--lc-space-md)]">
        <p className="m-0 text-[length:var(--lc-type-caption)] uppercase tracking-[var(--lc-tracking-overline)] text-[var(--lc-text-muted)]">
          {t('insufficientIntro')}
        </p>
        <p className="m-0 mt-[var(--lc-space-2xs)] text-[length:var(--lc-type-body-lg)] text-[var(--lc-text-primary)]">
          {reason}
        </p>
      </blockquote>
      <p className="m-0 mt-[var(--lc-space-md)] text-[length:var(--lc-type-body)] text-[var(--lc-text-secondary)]">
        {needsArea ? t('insufficientFixLocation') : t('insufficientFixPool')}
      </p>
    </section>
  )
}

function HistorySlot({ t }: { t: T }) {
  return (
    <div
      data-testid="history-slot"
      aria-disabled="true"
      className="flex items-center justify-between rounded-[var(--lc-radius-lg)] border border-dashed border-[var(--lc-border-strong)] bg-[var(--lc-surface-sunken)] px-[var(--lc-space-md)] py-[var(--lc-space-sm)]"
    >
      <span className="text-[length:var(--lc-type-body)] text-[var(--lc-text-muted)]">
        {t('historyLabel')}
      </span>
      <span className="text-[length:var(--lc-type-caption)] text-[var(--lc-text-muted)]">
        {t('historyTooShort')}
      </span>
    </div>
  )
}
